import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Alert, Grid, Typography } from '@mui/material';
import useImages from 'api/useImages';
import usePermissions from 'api/usePermissions';
import useReceiptsTheme from 'api/useReceiptsTheme';
import { DEFAULT_ASSET_URLS } from 'constants/constants';
import { EAssetType } from 'constants/enums';
import { useFormikContext } from 'formik';
import { UploadsTooltips } from 'hooks/useUpload';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import SwitchButton from 'components/SwitchButton/SwitchButton';
import { Flex } from 'components/ui/flex';

import { ThemeLoader } from '../ThemeLoader';

import { defaultReceiptTheme } from './defaults';
import ReceiptPreview from './ReceiptPreview';
import { ReceiptThemeFormValues } from './types';

import 'style/forms.scss';

const ReceiptTheme = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<ReceiptThemeFormValues>();
  const { publisherId } = useParams();
  const { isSuperAdminByProject } = usePermissions();

  const isSuperAdmin = isSuperAdminByProject(publisherId || '');

  const defaultValues = useMemo(() => {
    if (!values) {
      return defaultReceiptTheme;
    }
    return {
      ...defaultReceiptTheme,
      ...values
    };
  }, [values]);

  const { getReceiptTheme } = useReceiptsTheme(publisherId);
  const [savedAddress, setSavedAddress] = useState<
    ReceiptThemeFormValues['companyAddressStructured']
  >(defaultValues.companyAddressStructured);

  useEffect(() => {
    setFieldValue(
      'headerImage',
      getReceiptTheme.data?.receiptTheme?.headerImage
    );
  }, [getReceiptTheme.data?.receiptTheme?.headerImage, setFieldValue]);

  const pictures = useImages(publisherId).getImages;

  useEffect(
    () => setIsLoading(pictures.isLoading || !pictures.data),
    [pictures?.isLoading, pictures?.data]
  );

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <Grid container className="formContent">
      <Grid item xs={5}>
        <Typography
          variant="h6"
          sx={{
            marginBottom: '12px',
            fontFamily: 'Inter, Montserrat, serif',
            fontWeight: '600'
          }}
        >
          Payment Receipt
        </Typography>
        {!pictures.isLoading && (
          <Flex className="gap-6">
            {isSuperAdmin && (
              <AcCard stackContainer={false}>
                <Grid container rowSpacing={2} columnSpacing={1.5}>
                  <Grid item xs={12}>
                    <AcSelect
                      header="Payment Receipt Template"
                      name="paymentReceiptTemplateName"
                      value={defaultValues.paymentReceiptTemplateName || ''}
                      dataTestBoxId="paymentReceiptTemplate"
                      dataTestListId="paymentReceiptTemplateList"
                      optionDataTestIdPrefix="paymentReceiptTemplateOption"
                      items={[
                        {
                          content: 'Translatable Payment Receipt Mail',
                          key: 'translated-payment-receipt-mail',
                          value: 'translated-payment-receipt-mail'
                        },
                        {
                          content:
                            'Payment Receipt Mail (Legacy, not Translatable)',
                          key: 'payment-receipt-mail',
                          value: 'payment-receipt-mail'
                        }
                      ]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.paymentReceiptTemplateName &&
                        Boolean(errors.paymentReceiptTemplateName)
                      }
                    />
                  </Grid>
                </Grid>
              </AcCard>
            )}
            <AcCard stackContainer={false}>
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcInput
                    header="Sender Name"
                    name="senderName"
                    value={defaultValues.senderName}
                    tooltip="The sender name shown in your customer's inbox. If left blank, the default account name will be used"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.senderName && Boolean(errors.senderName)}
                  />
                </Grid>
              </Grid>
            </AcCard>
            <AcCard stackContainer={false}>
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Receipt Logo"
                    name="logo"
                    value={defaultValues.logo}
                    dataTestBoxId="receiptLogo"
                    uploadBtnTestId="uploadReceiptLogo"
                    dataTestListId="receiptLogoList"
                    optionDataTestIdPrefix="receiptLogoOption"
                    uploadConfig={{
                      onUploadSuccess: async (url: string) => {
                        await pictures.refetch();
                        setFieldValue('logo', url);
                      },
                      uploadType: EAssetType.RECEIPT_LOGO,
                      uploadMessage: UploadsTooltips[EAssetType.LOGO]
                    }}
                    items={[
                      {
                        content: 'Default Logo',
                        key: DEFAULT_ASSET_URLS.LOGO,
                        value: DEFAULT_ASSET_URLS.LOGO,
                        renderFunction: () => {
                          return (
                            <Flex direction="row" align="center" className="gap-2">
                              <img
                                src={DEFAULT_ASSET_URLS.LOGO}
                                alt="Default Logo"
                                style={{ width: 26, maxHeight: 30 }}
                              />
                              <Typography>Default Logo</Typography>
                            </Flex>
                          );
                        }
                      },
                      ...(pictures.data || [])
                        .filter(
                          (p: any) =>
                            p.type === EAssetType.RECEIPT_LOGO &&
                            (!p.mimeType ||
                              p.mimeType === 'image/png' ||
                              p.mimeType === 'image/jpeg')
                        )
                        .map((picture: any) => {
                          return {
                            content: picture.name,
                            key: picture.url,
                            value: picture.url,
                            renderFunction: () => {
                              return (
                                <Flex
                                  direction="row"
                                  align="center"
                                  className="gap-2"
                                >
                                  <img
                                    src={picture.url}
                                    alt={picture.name}
                                    style={{ width: 26, maxHeight: 30 }}
                                  />
                                  <Typography>{picture.name}</Typography>
                                </Flex>
                              );
                            }
                          };
                        })
                    ]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.logo && Boolean(errors.logo)}
                  />
                </Grid>
              </Grid>
            </AcCard>
            <AcCard stackContainer={false}>
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Receipt Header"
                    name="headerImage"
                    value={defaultValues.headerImage}
                    dataTestBoxId="receiptHeader"
                    uploadBtnTestId="uploadReceiptHeader"
                    dataTestListId="receiptHeaderList"
                    optionDataTestIdPrefix="receiptHeaderOption"
                    uploadConfig={{
                      onUploadSuccess: async (url: string) => {
                        await pictures.refetch();
                        setFieldValue('headerImage', url);
                      },
                      uploadType: EAssetType.RECEIPT_HEADER,
                      uploadMessage: UploadsTooltips[EAssetType.RECEIPT_HEADER]
                    }}
                    items={[
                      {
                        content: 'Default Header',
                        key: DEFAULT_ASSET_URLS.RECEIPT_HEADER,
                        value: DEFAULT_ASSET_URLS.RECEIPT_HEADER,
                        renderFunction: () => {
                          return (
                            <Flex direction="row" align="center" className="gap-2">
                              <img
                                src={DEFAULT_ASSET_URLS.RECEIPT_HEADER}
                                alt="Default Header"
                                style={{ width: 26, maxHeight: 30 }}
                              />
                              <Typography>Default Header</Typography>
                            </Flex>
                          );
                        }
                      },
                      ...(pictures.data || [])
                        .filter(
                          (p: any) =>
                            p.type === EAssetType.RECEIPT_HEADER &&
                            (!p.mimeType ||
                              p.mimeType === 'image/png' ||
                              p.mimeType === 'image/jpeg')
                        )
                        .map((picture: any) => {
                          return {
                            content: picture.name,
                            key: picture.url,
                            value: picture.url,
                            renderFunction: () => {
                              return (
                                <Flex
                                  direction="row"
                                  align="center"
                                  className="gap-2"
                                >
                                  <img
                                    src={picture.url}
                                    alt={picture.name}
                                    style={{ width: 26, maxHeight: 30 }}
                                  />
                                  <Typography>{picture.name}</Typography>
                                </Flex>
                              );
                            }
                          };
                        })
                    ]}
                    onChange={(event) => {
                      handleChange(event);
                      setFieldValue('headerImage', event.target.value);
                    }}
                    onBlur={handleBlur}
                    error={touched.headerImage && Boolean(errors.headerImage)}
                  />
                </Grid>
              </Grid>
            </AcCard>
            <AcCard stackContainer={false}>
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12} sx={{ marginLeft: '6px' }}>
                  <SwitchButton
                    status={Boolean(defaultValues.addFooter)}
                    texts={['Add Footer', 'Add Footer']}
                    functions={[
                      () => {
                        setFieldValue('addFooter', false);
                        setSavedAddress(
                          values.companyAddressStructured || null
                        );
                        setFieldValue('companyAddressStructured', null);
                      },
                      () => {
                        setFieldValue('addFooter', true);
                        if (!values.companyAddressStructured) {
                          setFieldValue(
                            'companyAddressStructured',
                            savedAddress || {
                              line1: '',
                              line2: '',
                              line3: ''
                            }
                          );
                        }
                      }
                    ]}
                    dataTestId="receiptAddFooterToggle"
                  />
                </Grid>
                {defaultValues.addFooter && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Footer is optional. If enabled, all three address lines
                      must be filled. Terms of Service, Privacy Policy, and
                      Support links are optional and use Appcharge defaults when
                      left blank.
                    </Alert>
                  </Grid>
                )}
                {defaultValues.addFooter && (
                  <>
                    <Grid item xs={12}>
                      <AcInput
                        header="Footer Line 1 (Company Name)"
                        name="companyAddressStructured.line1"
                        value={
                          defaultValues.companyAddressStructured?.line1 || ''
                        }
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        dataTestId="receiptFooterLine1"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AcInput
                        header="Footer Line 2 (Street Address)"
                        name="companyAddressStructured.line2"
                        value={
                          defaultValues.companyAddressStructured?.line2 || ''
                        }
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        dataTestId="receiptFooterLine2"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AcInput
                        header="Footer Line 3 (City, Country)"
                        name="companyAddressStructured.line3"
                        value={
                          defaultValues.companyAddressStructured?.line3 || ''
                        }
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        dataTestId="receiptFooterLine3"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <AcInput
                        header="Terms of Service URL"
                        name="termsOfServiceUrl"
                        value={defaultValues.termsOfServiceUrl || ''}
                        tooltip="Optional. If left blank, a default Appcharge Terms link will be used."
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        enableNewTabOpen
                        dataTestId="receiptTermsUrl"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <AcInput
                        header="Privacy Policy URL"
                        name="privacyPolicyUrl"
                        value={defaultValues.privacyPolicyUrl || ''}
                        tooltip="Optional. If left blank, a default Appcharge Privacy link will be used."
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        enableNewTabOpen
                        dataTestId="receiptPrivacyUrl"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AcInput
                        header="Support URL"
                        name="supportUrl"
                        value={defaultValues.supportUrl || ''}
                        tooltip="Optional. If left blank, a default Appcharge Support link will be used."
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        enableNewTabOpen
                        dataTestId="receiptSupportUrl"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </AcCard>
          </Flex>
        )}
      </Grid>
      <Grid item xs={7} className="iphone-mock-wrapper">
        <div className="store-preview-wrapper" style={{ overflow: 'visible' }}>
          <ReceiptPreview
            receiptLogo={defaultValues.logo}
            receiptHeader={
              defaultValues.headerImage || DEFAULT_ASSET_URLS.RECEIPT_HEADER
            }
            addFooter={Boolean(defaultValues.addFooter)}
            companyAddressStructured={
              defaultValues.companyAddressStructured || undefined
            }
            termsOfServiceUrl={defaultValues.termsOfServiceUrl}
            privacyPolicyUrl={defaultValues.privacyPolicyUrl}
            supportUrl={defaultValues.supportUrl || undefined}
          />
        </div>
      </Grid>
    </Grid>
  );
};

export default ReceiptTheme;
